// Run Detail page — PRD-0018 Phase 5
// NOTE: Transcript and Events sections are explicitly out of scope (no per-run
// transcript or events log exists in the DB). Do not add them.

import { SeoResearchReport } from "../components/research/SeoResearchReport";
import { SourceCollectionReport } from "../components/research/SourceCollectionReport";
import { useState, useEffect, useRef } from "react";
import { actionInstanceRunReference } from "../../../shared/action-instance-run";
import { blogGateFailureFromRunOutput } from "../../../shared/blog-gate-failure";
import { BlogGateFindings } from "../components/campaign/BlogGateFailure";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { useAddToCorpus } from "../hooks/useBrand";
import { useRun } from "../hooks/useRun";
import { useRunTrace } from "../hooks/useRunTrace";
import { useTaskRunBrief } from "../hooks/useTaskRunBrief";
import { useMarketResearchRunOutput } from "../hooks/useMarketResearchRunOutput";
import { buildBrandScopedPath, parseBrandIdFromSearch } from "../lib/brand-navigation";
import { buildRunViewPath } from "../lib/run-view-navigation";
import { RUN_VIEW_CONTAINER_CLASS } from "../lib/run-view-layout";
import { timeAgo } from "../lib/utils";
import { workObjectDisplaySpec, type WorkObjectDisplayState } from "../lib/work-object-display";
import { resolveWorkspaceReturn, workspaceReturnLocationState } from "../lib/workspace-return";
import { runOutcome, runOutcomeBadgeVariant } from "../lib/run-outcome";
import { taskRunPresentation, taskRunUsesResultView } from "../lib/task-run-presentation";
import { useOptionalWorkspaceTabs } from "../contexts/WorkspaceTabsContext";
import { Badge } from "../components/ui/Badge";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { FindingsList } from "../components/runs/FindingsList";
import { api, ApiError } from "../lib/api";
import { OutputView } from "../components/output/OutputView";
import { Markdown } from "../components/agent/Markdown";
import { workProductWarningDefinition } from "../../../shared/types";
import type { MarketResearchReview, TaskRun, TaskRunBriefEvidence, WorkProductReadiness } from "../../../shared/types";

function humaniseSlug(slug: string): string {
  return slug.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Visible when there is something worth showing — avoids rendering `{}` or `[]`. */
export function hasRenderableInput(input: unknown): boolean {
  if (input === null || input === undefined) return false;
  if (Array.isArray(input)) return input.length > 0;
  if (typeof input === "object") return Object.keys(input as object).length > 0;
  return true;
}

function humaniseDocumentType(documentType: string | null | undefined): string {
  const trimmedDocumentType = documentType?.trim();
  return trimmedDocumentType ? humaniseSlug(trimmedDocumentType) : "Unknown Type";
}

function shortLabel(value: string | null | undefined, prefix: string): string | null {
  return value?.trim() ? `${prefix} ${value.slice(0, 8)}` : null;
}

function workProductReference(product: {
  document_key?: string | null;
  artifact_key?: string | null;
  storage_key?: string | null;
}): string | null {
  if (product.document_key) return `Document ${product.document_key}`;
  if (product.artifact_key) return `Artifact ${product.artifact_key}`;
  if (product.storage_key) return `Storage ${product.storage_key}`;
  return null;
}

export function isContentDraftRun(taskSlug: string | null | undefined): boolean {
  if (!taskSlug) return false;
  if (taskSlug === "content_draft") return true;
  // Transitional compatibility for the legacy YouTube alias. New runs should
  // use the base `content_draft` slug plus scoped surface params instead.
  if (taskSlug === "content_draft_youtube_personal") return true;
  // Backend catalog compound slugs use one scope segment with at least one ASCII letter or digit.
  return /^content_draft:(?=[^:\s]*[A-Za-z0-9])[^:\s]+$/.test(taskSlug);
}

function isReviewWorkProductRun(taskSlug: string | null | undefined): boolean {
  return isContentDraftRun(taskSlug) || taskSlug === "voice_review";
}

function runWorkObjectState(status: TaskRun["status"]): WorkObjectDisplayState {
  if (status === "failed" || status === "skipped") return status;
  return "unknown";
}

function reviewOutputDisplayState(documentType: string | null | undefined): WorkObjectDisplayState {
  return documentType === "voice_review" ? "open" : "unknown";
}

function truncatePreview(content: string, maxLength = 520): string {
  const normalized = content.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trimEnd()}…`;
}

function marketResearchQuestion(run: TaskRun): string | null {
  const prompt = run.input?.prompt;
  return typeof prompt === "string" && prompt.trim() ? prompt.trim() : null;
}

function currentEventsCampaignQuestion(run: TaskRun): string | null {
  const question = run.input?.campaign_question;
  return typeof question === "string" && question.trim() ? question.trim() : null;
}

/**
 * A run detail URL can outlive a brand merge or a previous navigation state.
 * Once the authenticated run read succeeds, its tenant is authoritative over
 * the URL's navigational hint. Keeping that one source of truth prevents the
 * shell, breadcrumbs, and Activity list from disagreeing about the Run.
 */
export function canonicalRunDetailPath(
  location: Pick<Location, "pathname" | "search" | "hash">,
  runBrandId: string | null | undefined,
): string | null {
  if (!runBrandId?.trim()) return null;
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const canonicalPath = buildBrandScopedPath(currentPath, runBrandId);
  return canonicalPath === currentPath ? null : canonicalPath;
}

export type TaskRunMarketerPresentation = {
  briefLabel: string;
  resultLabel: string;
  evidenceLabel: string;
};

/** The detail consumes the API-owned presentation projection, never raw output metadata. */
export function taskRunMarketerPresentation(run: Pick<TaskRun, "task_slug" | "presentation">): TaskRunMarketerPresentation {
  const labels = taskRunPresentation(run);
  return {
    briefLabel: labels.brief_label,
    resultLabel: labels.result_label,
    evidenceLabel: labels.evidence_label,
  };
}

export function SupportingFindingsPanel({
  label,
  findings,
  isLoading = false,
  error = null,
}: {
  label: string;
  findings: TaskRunBriefEvidence[];
  isLoading?: boolean;
  error?: unknown;
}) {
  const knownFindingCount = !isLoading && !error;
  const groups = new Map<string, { label: string; description: string; findings: TaskRunBriefEvidence[] }>();
  const order = ["web_citation", "search_signal", "generated_observation", "partial_or_unknown"];
  for (const finding of findings) {
    const kind = finding.presentation_kind ?? (safeExternalHref(finding.source_url) ? "web_citation" : "partial_or_unknown");
    const defaults = kind === "web_citation"
      ? { label: "Web sources", description: "Published web pages linked from this result." }
      : kind === "search_signal"
        ? { label: "Search signals", description: "Questions and search patterns captured during this research. These are not individual web pages." }
        : kind === "generated_observation"
          ? { label: "Generated observations", description: "Observations produced from the Run’s recorded analysis rather than a separately linkable page." }
          : { label: "Evidence with limited source detail", description: "This evidence is retained, but its recorded source detail is incomplete." };
    const existing = groups.get(kind) ?? {
      label: finding.presentation_group_label ?? defaults.label,
      description: finding.presentation_group_description ?? defaults.description,
      findings: [],
    };
    existing.findings.push(finding);
    groups.set(kind, existing);
  }
  return (
    <details className="rounded-xl border border-slate-200 bg-white p-4">
      <summary className="cursor-pointer text-sm font-semibold text-slate-900">{label}{knownFindingCount ? ` (${findings.length})` : ""}</summary>
      <p className="mt-2 text-sm text-slate-600">Sources and observations used in this result.</p>
      {isLoading ? <p className="mt-3 text-sm text-slate-600" role="status">Loading supporting findings…</p> : error ? (
        <p className="mt-3 text-sm text-amber-800" role="alert">Supporting findings are unavailable right now. Open Traces for the retrieval status.</p>
      ) : findings.length === 0 ? <p className="mt-3 text-sm text-slate-600">No supporting findings were recorded for this result.</p> : (
        <div className="mt-3 space-y-5">
          {order.flatMap((kind) => {
            const group = groups.get(kind);
            if (!group) return [];
            return <section key={kind} aria-label={group.label}>
              <h3 className="text-sm font-semibold text-slate-900">{group.label} ({group.findings.length})</h3>
              <p className="mt-1 text-xs leading-5 text-slate-600">{group.description}</p>
              <div className="mt-2 space-y-3">
                {group.findings.map((finding) => {
                  const href = safeExternalHref(finding.source_url);
                  const source = finding.source_label ?? finding.domain ?? finding.source_type ?? null;
                  const date = finding.published_at ? new Date(finding.published_at).toLocaleDateString() : null;
                  return <article key={finding.finding_id} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div><h4 className="text-sm font-medium text-slate-900">{finding.title}</h4>{source || date ? <p className="mt-1 text-xs text-slate-500">{[source, date].filter(Boolean).join(" · ")}</p> : null}</div>
                      {href ? <a href={href} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-700 hover:underline">Open source ↗</a> : null}
                    </div>
                  </article>;
                })}
              </div>
            </section>;
          })}
        </div>
      )}
    </details>
  );
}

export function marketResearchReviewCopy(review: MarketResearchReview | null | undefined): { label: string; description: string } {
  if (review?.state === "ready_to_review") {
    return { label: "Ready to review", description: "Your research report is ready. Review the findings and sources below before deciding what to do next." };
  }
  if (review?.state === "needs_attention") {
    if (review.reason === "job_failed") return { label: "Needs attention", description: "This research Run did not finish. Rerun the question to prepare a complete report." };
    if (review.reason === "analysis_blocked") return { label: "Needs attention", description: "This question could not be researched with the available inputs. Update the question and try again." };
    if (review.reason === "canonical_output_unreadable") return { label: "Needs attention", description: "Research finished, but the report cannot be opened right now. Reload once, then rerun if it remains unavailable." };
    if (review.reason === "canonical_output_malformed") return { label: "Needs attention", description: "Research finished, but the report is incomplete. You can rerun the research safely." };
    return { label: "Needs attention", description: "Research finished without a report to review. You can rerun the question safely." };
  }
  return { label: "Working", description: "HelloConvo is gathering and synthesizing information for this question. This page will update when the report is ready." };
}

function stringOutputValue(output: Record<string, unknown> | null | undefined, key: string): string | null {
  const value = output?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}

function nestedOutputString(output: Record<string, unknown> | null | undefined, key: string): string | null {
  const direct = stringOutputValue(output, key);
  if (direct) return direct;
  const metadata = output?.metadata;
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  return stringOutputValue(metadata as Record<string, unknown>, key);
}

function briefResolverReason(error: unknown): { reason: string | null; details: Record<string, unknown> | null } {
  const payload = error instanceof ApiError
    ? error.details
    : (
      error
      && typeof error === "object"
      && "details" in error
      && typeof (error as { details?: unknown }).details === "object"
      && (error as { details?: unknown }).details !== null
      && !Array.isArray((error as { details?: unknown }).details)
        ? (error as { details: Record<string, unknown> }).details
        : null
    );
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return { reason: null, details: null };
  const reason = typeof (payload as { reason?: unknown }).reason === "string"
    ? (payload as { reason: string }).reason
    : null;
  const details = (payload as { details?: unknown }).details;
  return {
    reason,
    details: details && typeof details === "object" && !Array.isArray(details)
      ? details as Record<string, unknown>
      : null,
  };
}

function readinessLabel(readiness: WorkProductReadiness | undefined): string | null {
  switch (readiness) {
    case "usable":
      return "Output ready";
    case "usable_with_warnings":
      return "Output warnings";
    case "unlinked":
      return "Output unlinked";
    case "diagnostic_only":
      return "Diagnostic only";
    case "failed":
      return "Failed";
    case "running":
      return "Running";
    default:
      return null;
  }
}

function readinessVariant(readiness: WorkProductReadiness | undefined): "success" | "warning" | "error" | "default" {
  switch (readiness) {
    case "usable":
      return "success";
    case "usable_with_warnings":
    case "unlinked":
    case "diagnostic_only":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

function warningText(code: string): string {
  const definition = workProductWarningDefinition(code);
  return `${definition.label}: ${definition.description}`;
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case "completed":
      return <Badge variant="success">Completed</Badge>;
    case "failed":
      return <Badge variant="error">Failed</Badge>;
    case "skipped":
      return <Badge variant="warning">Skipped</Badge>;
    case "running":
      return <Badge variant="info">Running</Badge>;
    case "retrying":
      return <Badge variant="warning">Retrying</Badge>;
    case "retried":
      return <Badge>Retried</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

interface SocialListeningActionPayload {
  primaryAction: string | null;
  actionRationale: string | null;
  corpusCandidateMd: string | null;
  notableThreads: Array<{ title: string | null; url: string | null; platform: string | null; reason: string | null }>;
}

function getSocialListeningActionPayload(
  output: Record<string, unknown> | null | undefined,
): SocialListeningActionPayload {
  const metadata =
    output && typeof output.metadata === "object" && output.metadata !== null && !Array.isArray(output.metadata)
      ? (output.metadata as Record<string, unknown>)
      : null;
  const source = metadata ?? output ?? null;
  const notableThreads = Array.isArray(source?.notable_threads)
    ? source.notable_threads.flatMap((item) => {
        if (typeof item !== "object" || item === null || Array.isArray(item)) return [];
        const record = item as Record<string, unknown>;
        return [{
          title: typeof record.title === "string" ? record.title : null,
          url: typeof record.url === "string" ? record.url : null,
          platform: typeof record.platform === "string" ? record.platform : null,
          reason: typeof record.reason === "string" ? record.reason : null,
        }];
      })
    : [];

  return {
    primaryAction:
      typeof source?.primary_action === "string" && source.primary_action.trim()
        ? source.primary_action
        : null,
    actionRationale:
      typeof source?.action_rationale === "string" && source.action_rationale.trim()
        ? source.action_rationale
        : null,
    corpusCandidateMd:
      typeof source?.corpus_candidate_md === "string" && source.corpus_candidate_md.trim()
        ? source.corpus_candidate_md
        : null,
    notableThreads,
  };
}

function safeExternalHref(rawHref: string | null | undefined): string | null {
  if (!rawHref?.trim()) return null;
  try {
    const parsed = new URL(rawHref);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function buildBrandWorkspacePath(brandId: string | null | undefined): string | null {
  if (!brandId?.trim()) return null;
  return `/brand/${encodeURIComponent(brandId)}`;
}

/** A campaign SEO scan projects its ideas into that campaign's Opportunities tab (PRD-0189 A); link back to them. */
export function researchIdeasPath(run: Pick<TaskRun, "task_slug" | "brand_id" | "campaign_id">): string | null {
  if (run.task_slug !== "market_research") return null;
  const campaignPath = buildCampaignDetailPath(run.brand_id, run.campaign_id);
  return campaignPath ? `${campaignPath}?view=opportunities` : null;
}

function buildCampaignDetailPath(brandId: string | null | undefined, campaignId: string | null | undefined): string | null {
  if (!brandId?.trim() || !campaignId?.trim()) return null;
  return `/brand/${encodeURIComponent(brandId)}/campaigns/${encodeURIComponent(campaignId)}`;
}

type RecoveryAction = {
  label: string;
  detail: string;
  cta: string;
  href?: string;
  kind: "link" | "retry" | "rerun";
};

export function recoveryActionForRun(
  run: TaskRun,
  brandId: string | null | undefined,
  allowedKinds: ReadonlySet<RecoveryAction["kind"]>,
): RecoveryAction | null {
  const campaignPath = buildCampaignDetailPath(run.brand_id, run.campaign_id);
  const tasksPath = buildBrandScopedPath("/tasks", brandId ?? run.brand_id);

  const permitted = (action: RecoveryAction): RecoveryAction | null =>
    allowedKinds.has(action.kind) ? action : null;

  if (run.collection?.dataset && run.status === "completed") return permitted({
    label: "Inspect saved dataset", detail: run.collection.state === "partial" ? "Inspect the partial snapshot and its coverage before using it." : "Open this Run's saved snapshot to inspect or download the collected data.",
    cta: "Open saved dataset", href: run.collection.dataset.route_path, kind: "link",
  });
  if (run.status === "completed" && run.task_slug.startsWith("dataset_query_sync:") && run.collection) return permitted({
    label: "Inspect saved dataset", detail: "This view could not verify the saved snapshot. Inspect this Action's selected saved data before starting new work.",
    cta: "Open dataset Action", href: run.collection.source_path, kind: "link",
  });
  if (((run.task_slug.startsWith("producer:") || run.task_slug === "source_analytics_sync") && ["failed", "skipped"].includes(run.status)) || ["evidence_unavailable", "collection_partial"].includes(run.state_projection?.operator_state ?? "")) {
    const sourceCollection = run.task_slug === "producer:provider_analytics_sync" || run.task_slug === "source_analytics_sync";
    return permitted({
      label: sourceCollection ? "Inspect source and saved results" : "Inspect saved results",
      detail: "Check source access and retained results before starting new work. This producer Run cannot be replayed through the generic Action retry.",
      cta: sourceCollection ? "Open source settings" : "Open Runs",
      href: sourceCollection
        ? `/brand/${encodeURIComponent(brandId ?? run.brand_id)}/settings/connections`
        : buildBrandScopedPath("/runs", brandId ?? run.brand_id),
      kind: "link",
    });
  }

  if (run.status === "failed") {
    return permitted({
      label: "Retry failed Run",
      detail: "Retry this Action with the same brand, campaign, brief, and params so the operator can confirm whether the failure was transient.",
      cta: "Retry Run",
      kind: "retry",
    });
  }

  if (run.status === "skipped") {
    return permitted({
      label: "Rerun skipped Action",
      detail: "Rerun this Action with the same scope after checking whether the original skip condition still applies.",
      cta: "Rerun Action",
      kind: "rerun",
    });
  }

  if (run.state_projection?.operator_state !== "completed_noop") return null;

  if (run.state_projection.prerequisite === "briefed_brief") {
    return permitted({
      label: "Create or advance briefed work",
      detail: "This content-draft run had nothing eligible to draft. Open the campaign queue and move a same-day brief into the briefed state before rerunning.",
      cta: campaignPath ? "Open campaign" : "Open Actions",
      href: campaignPath ?? tasksPath,
      kind: "link",
    });
  }

  if (run.state_projection.prerequisite === "eligible_content_idea") {
    return permitted({
      label: "Create an eligible content idea",
      detail: "This ideation-briefing run could not advance anything because no saved idea was ready to brief. Open the campaign queue, add or refine an idea, then rerun.",
      cta: campaignPath ? "Open campaign" : "Open Actions",
      href: campaignPath ?? tasksPath,
      kind: "link",
    });
  }

  return permitted({
    label: "Inspect Action prerequisites",
    detail: "This Run completed as a no-op. Check the Action queue and campaign state before rerunning so the next attempt has eligible work.",
    cta: campaignPath ? "Open campaign" : "Open Actions",
    href: campaignPath ?? tasksPath,
    kind: "link",
  });
}

export function RunDetail() {
  const { runId } = useParams<{ runId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const workspaceTabs = useOptionalWorkspaceTabs();
  const brandId = parseBrandIdFromSearch(location.search);

  const { data: res, isLoading, isError, refetch } = useRun(runId);
  const run = res?.data;
  const runBrandId = run?.brand_id ?? brandId;
  const workspaceReturn = resolveWorkspaceReturn(location, run?.brand_id);
  // Snapshot-backed Runs select their bounded result renderer from the Action
  // contract. Historic rows retain their durable artifact fallbacks until they
  // are migrated; neither path branches on a task slug.
  const initialPresentation = run ? taskRunPresentation(run) : null;
  // Result views are mutually exclusive: an Action declares exactly one. Only the
  // fallbacks below could both fire, which rendered the market-research report section
  // and the current-events section on the same page — so a current-events run with no
  // evidence still advertised a report to reload. Current events wins that overlap.
  const currentEventsResearchRun = initialPresentation?.result_view === "current_events_campaign_research"
    || (!initialPresentation?.result_view && Boolean(
      run?.work_products?.some((product) => product.family === "current_events_research_artifact"),
    ));
  const marketResearchRun = initialPresentation?.result_view === "market_research_review"
    || (!initialPresentation?.result_view && !currentEventsResearchRun && Boolean(run?.market_research_review));
  const socialListeningActionRun = Boolean(run && taskRunUsesResultView(run, "social_listening_action"));
  const marketResearchOutput = useMarketResearchRunOutput(
    runId,
    // A skipped run's stale ready_to_review must not fetch a report the page then
    // suppresses; status is authoritative here as it is for the header copy below.
    Boolean(
      marketResearchRun
        && run?.status !== "skipped"
        && run?.market_research_review?.state === "ready_to_review",
    ),
  );
  // Result evidence must be available before a person opens the technical
  // drawer. The drawer only controls its visual disclosure, not the data read.
  const diagnosticsEnabled = Boolean(runId && run && !isError);
  const { data: traceRes, error: traceError, isLoading: traceLoading } = useRunTrace(runId, diagnosticsEnabled);
  const { data: briefRes, error: briefError, isLoading: briefLoading } = useTaskRunBrief(runId, diagnosticsEnabled);
  const addToCorpus = useAddToCorpus(run?.brand_id ?? "");
  const lastSeenRunIdRef = useRef<string | null>(null);

  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  useEffect(() => {
    const canonicalPath = canonicalRunDetailPath(location, run?.brand_id);
    if (canonicalPath) {
      navigate(canonicalPath, {
        replace: true,
        // Keep only the allow-listed origin source across the URL correction.
        // It is the only Run Detail navigation state we intentionally support.
        state: workspaceReturnLocationState(workspaceReturn.source),
      });
    }
  }, [location, navigate, run?.brand_id, workspaceReturn.source]);

  useEffect(() => {
    if (run) {
      document.title = `${taskRunPresentation(run).job_label} Result — HelloConvo`;
    } else {
      document.title = "Result — HelloConvo";
    }
    return () => {
      document.title = "HelloConvo";
    };
  }, [run]);

  useEffect(() => {
    if (!run) return;
    workspaceTabs?.renameTab(`runs:${run.id}`, taskRunPresentation(run).job_label);
  }, [workspaceTabs, run]);

  useEffect(() => {
    if (!run) return;
    if (lastSeenRunIdRef.current === null) {
      lastSeenRunIdRef.current = run.id;
      return;
    }
    if (lastSeenRunIdRef.current !== run.id) {
      addToCorpus.reset();
      lastSeenRunIdRef.current = run.id;
    }
  }, [addToCorpus.reset, run?.id]);

  async function handleRetry() {
    if (!runId) return;
    setRetrying(true);
    setRetryError(null);
    try {
      const result = await api.post<{ data: { runId: string } }>(
        `/api/task-runs/${runId}/retry`,
        {},
      );
      setRetrying(false);
      navigate(buildBrandScopedPath(`/runs/${result.data.runId}`, runBrandId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setRetryError("This run has already been retried.");
        void refetch(); // Refresh status so Retry button reflects updated state
      } else {
        setRetryError(err instanceof Error ? err.message : "Failed to retry run.");
      }
      setRetrying(false);
    }
  }

  async function handleRerun() {
    if (!runId) return;
    setRetrying(true);
    setRetryError(null);
    try {
      const result = await api.post<{ data: { runId: string } }>(
        `/api/task-runs/${runId}/rerun`,
        {},
      );
      setRetrying(false);
      navigate(buildBrandScopedPath(`/runs/${result.data.runId}`, runBrandId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setRetryError("This run cannot be rerun right now.");
        void refetch();
      } else {
        setRetryError(err instanceof Error ? err.message : "Failed to rerun run.");
      }
      setRetrying(false);
    }
  }

  if (isLoading) {
    return (
      <div className="job-result-frame mx-auto flex min-h-[40vh] w-full max-w-7xl items-center justify-center px-4 py-4 md:px-6" data-testid="job-result-frame">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (isError || !run) {
    return (
      <div className={`${RUN_VIEW_CONTAINER_CLASS} job-result-frame`} data-testid="job-result-frame">
        <WorkspaceHeader
          eyebrow="Result"
          title="Result unavailable"
          description="This Run's Result could not be loaded or is no longer available."
          breadcrumbs={[
            { label: "Workspace home", href: "/home" },
            { label: workspaceReturn.label, href: workspaceReturn.href },
            { label: "Result" },
          ]}
        />
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center">
          <p className="text-slate-600">{isError ? "This run could not be reached." : "Run not found."}</p>
          {isError && (
            <button type="button" onClick={() => void refetch()} className="mt-3 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
              Try again
            </button>
          )}
          <Link to={workspaceReturn.href} className="mt-2 inline-block text-sm text-blue-600 hover:underline">
            ← Back to {workspaceReturn.label}
          </Link>
        </div>
      </div>
    );
  }

  const currentRun = run;
  const savedActionRun = actionInstanceRunReference(currentRun.output);
  const runPresentation = taskRunPresentation(currentRun);
  // Snapshot-backed Runs may surface only the recovery affordances declared by
  // the Action at launch. Legacy Runs preserve their historic retry behavior.
  const allowedRecoveryKinds = new Set<RecoveryAction["kind"]>(
    savedActionRun ? ["link"] : (runPresentation.recovery_kinds?.filter((kind): kind is RecoveryAction["kind"] => kind !== "none")
      ?? ["retry", "rerun", "link"]),
  );
  const canUseRecovery = (kind: RecoveryAction["kind"]) => allowedRecoveryKinds.has(kind);
  const marketerPresentation = taskRunMarketerPresentation(currentRun);
  const marketResearchTitle = runPresentation.job_label;
  const rawResearchReview = currentRun.market_research_review
    ?? (marketResearchRun
      ? currentRun.status === "completed" || currentRun.status === "failed"
        ? { state: "needs_attention" as const, reason: "canonical_output_missing" as const, output_id: null, route_path: null }
        : { state: "working" as const, reason: null, output_id: null, route_path: null }
      : null);
  // Run status is authoritative over a review attached before the run was skipped.
  // Without this a skipped run kept its stale `ready_to_review` and the page announced
  // "Your research report is ready" for research that never ran.
  // The run's own state outranks a market-research review attached before the run was
  // skipped; without this a skipped run announced a ready report.
  const skippedOutcome = currentRun.status === "skipped" ? runOutcome(currentRun) : null;
  // Any skipped run normalises to needs_attention, not just one carrying a stale
  // ready_to_review: leaving it "working" also withheld the rerun action, so a skipped
  // run offered no recovery at all.
  const researchReview = skippedOutcome && rawResearchReview
    ? { ...rawResearchReview, state: "needs_attention" as const, reason: "analysis_blocked" as const }
    : rawResearchReview;
  const researchReviewCopy = skippedOutcome
    ? { label: skippedOutcome.label, description: skippedOutcome.description }
    : marketResearchReviewCopy(researchReview);
  const researchQuestion = marketResearchQuestion(currentRun);
  const researchOutputDocument = marketResearchOutput.data?.data.output_document ?? null;
  const researchOutputUnavailable = researchReview?.state === "ready_to_review"
    && (marketResearchOutput.isError || (!marketResearchOutput.isLoading && !researchOutputDocument));
  const trace = traceRes?.data ?? null;
  const traceSupportingFindings = Array.isArray(trace?.supporting_findings) ? trace.supporting_findings : [];
  const traceSourceRuns = Array.isArray(trace?.source_runs) ? trace.source_runs : [];

  const durationMs = currentRun.duration_ms;
  const durationLabel =
    durationMs !== null
      ? durationMs < 1000
        ? `${durationMs}ms`
        : `${Math.round(durationMs / 1000)}s`
      : null;
  const summary =
    currentRun.state_projection?.summary
    ?? (currentRun.output && typeof currentRun.output.summary === "string" ? currentRun.output.summary : null);
  // Walkthrough item 4: a blocked blog draft explains itself here, not only in the raw output.
  const blogGateFailure = currentRun.status === "failed" && currentRun.task_slug === "blog_compose_for_review"
    ? blogGateFailureFromRunOutput(currentRun.output) : null;
  const blogGateBriefId = currentRun.input && typeof currentRun.input === "object" && typeof (currentRun.input as Record<string, unknown>).target_brief_id === "string"
    ? (currentRun.input as Record<string, string>).target_brief_id : null;
  const blogGateBriefHref = blogGateFailure && blogGateBriefId && currentRun.campaign_id
    ? `/brand/${encodeURIComponent(currentRun.brand_id)}/campaigns/${encodeURIComponent(currentRun.campaign_id)}/briefs/${encodeURIComponent(blogGateBriefId)}?document=brief`
    : null;
  const appliedCorrectionsCount =
    currentRun.output && typeof currentRun.output.applied_corrections_count === "number"
      ? currentRun.output.applied_corrections_count
      : 0;
  const workProducts = Array.isArray(currentRun.work_products) ? currentRun.work_products : [];
  const showPageReadinessBadge = Boolean(readinessLabel(currentRun.readiness));
  const briefDetail = briefRes?.data;
  const workProductDocumentKey = nestedOutputString(currentRun.output, "document_key");
  const workProductBriefId = nestedOutputString(currentRun.output, "brief_id") ?? currentRun.brief_id;
  // A review-specific output contract determines whether this is draft/review
  // work. A generic Action may also attach a brief without becoming a draft.
  const reviewWorkProductRun = isReviewWorkProductRun(currentRun.task_slug)
    || workProducts.some((product) => product.output_type === "draft" || product.output_type === "voice_review")
    || briefDetail?.document_type === "draft"
    || briefDetail?.document_type === "voice_review";
  const contentDraftRun = isContentDraftRun(currentRun.task_slug)
    || workProducts.some((product) => product.output_type === "draft")
    || briefDetail?.document_type === "draft";
  const reviewWorkProductHasDocument = reviewWorkProductRun && !!briefDetail;
  const reviewWorkProductMissingDocument =
    reviewWorkProductRun && currentRun.status === "completed" && !briefDetail && !briefLoading;
  const resolverInfo = briefResolverReason(briefError);
  const parsedSocialAction = getSocialListeningActionPayload(currentRun.output);
  const socialAction = socialListeningActionRun && (parsedSocialAction.corpusCandidateMd || parsedSocialAction.actionRationale || parsedSocialAction.primaryAction)
    ? parsedSocialAction
    : null;
  const canSaveToCorpus = !!socialAction?.corpusCandidateMd?.trim();
  const socialListeningNextStepCopy = socialAction
    ? (
      socialAction.actionRationale
      ?? (
        canSaveToCorpus
          ? "Save the durable insights from this run to Corpus so future briefs and agents can reuse them."
          : "This run did not surface any durable insights to save yet. Check back after new mentions are collected or rerun it when the topic changes."
      )
    )
    : null;
  const stateProjection = currentRun.state_projection ?? null;
  const recoveryAction = recoveryActionForRun(currentRun, runBrandId, allowedRecoveryKinds);
  const workspaceBrandPath = buildBrandWorkspacePath(currentRun.brand_id);
  const campaignDetailPath = buildCampaignDetailPath(currentRun.brand_id, currentRun.campaign_id);
  const reviewPath = (reviewWorkProductRun || briefDetail)
    ? buildBrandScopedPath(`/review/${currentRun.id}`, runBrandId)
    : null;
  const runDisplaySpec = workObjectDisplaySpec("task_run", runWorkObjectState(currentRun.status));
  const runPrimaryActionLabel = (
    (currentRun.status === "failed" && recoveryAction?.kind === "retry")
    || (currentRun.status === "skipped" && recoveryAction?.kind === "rerun")
  ) ? runDisplaySpec.primaryCta.label : null;
  const briefDisplaySpec = briefDetail
    ? workObjectDisplaySpec(briefDetail.document_type, reviewOutputDisplayState(briefDetail.document_type))
    : null;
  const primaryOutput = workProducts[0] ?? null;
  const currentEventsOutput = currentEventsResearchRun
    ? workProducts.find((product) => product.family === "current_events_research_artifact") ?? null
    : null;
  const hasUsableCurrentEventsReport = Boolean(
    currentEventsOutput?.route_path
    && (currentEventsOutput.readiness === "usable" || currentEventsOutput.readiness === "usable_with_warnings"),
  );
  const currentEventsOutcome = currentEventsResearchRun
    ? runOutcome(currentRun, hasUsableCurrentEventsReport)
    : null;
  const seoResearch = currentRun.seo_research;
  const seoOutcome = seoResearch ? runOutcome(currentRun) : null;
  const showGenericRunDetails = !marketResearchRun && !currentEventsResearchRun && !seoResearch;
  const primarySourceFinding = traceSupportingFindings[0] ?? null;
  const primarySourceFindingHref = safeExternalHref(primarySourceFinding?.source_url);

  async function handleSaveToCorpus() {
    if (!canSaveToCorpus || !socialAction?.corpusCandidateMd?.trim()) return;
    await addToCorpus.mutateAsync({
      content: socialAction.corpusCandidateMd,
      metadata: {
        source: "task_run",
        task_slug: currentRun.task_slug,
        run_id: currentRun.id,
      },
    });
  }

  return (
    <div className={`${RUN_VIEW_CONTAINER_CLASS} job-result-frame`} data-testid="job-result-frame">
      <div
        data-run-detail-header="true"
        className="[&_[data-heading-row]]:flex-col md:[&_[data-heading-row]]:flex-row"
      >
        <WorkspaceHeader
          eyebrow="Result"
          title={runPresentation.job_label}
          description={marketResearchRun
            ? researchQuestion ?? "Research a focused market question."
            : currentEventsResearchRun
              ? currentEventsCampaignQuestion(run) ?? "Research recent events for a campaign decision."
            : stateProjection && ["failed", "stale", "completed_noop", "skipped", "evidence_unavailable", "collection_partial"].includes(stateProjection.operator_state)
              ? stateProjection.label
              : "Review the Run outcome and supporting evidence below."}
          breadcrumbs={[
            { label: "Workspace home", href: "/home" },
            { label: workspaceReturn.label, href: workspaceReturn.href },
            { label: runPresentation.job_label },
            { label: "Result" },
          ]}
          badges={seoOutcome ? <Badge variant={runOutcomeBadgeVariant(seoOutcome.state)}>{seoOutcome.label}</Badge> : marketResearchRun ? <Badge variant={runOutcomeBadgeVariant(skippedOutcome ? skippedOutcome.state : researchReview?.state === "ready_to_review" ? "ready" : researchReview?.state === "needs_attention" ? "needs_attention" : "working")}>{researchReviewCopy.label}</Badge> : currentEventsResearchRun ? (
            <>
              <Badge variant={runOutcomeBadgeVariant(currentEventsOutcome!.state)}>{currentEventsOutcome!.label}</Badge>
            </>
          ) : (
            <>
              <Badge>{runDisplaySpec.typeLabel}</Badge><StatusBadge status={run.status} />
              {showPageReadinessBadge && (
                <Badge variant={readinessVariant(currentRun.readiness)}>
                  {readinessLabel(currentRun.readiness)}
                </Badge>
              )}
            </>
          )}
          metadata={(
            <>
              {showGenericRunDetails && <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
                Started {timeAgo(run.started_at)}
              </span>}
              {run.completed_at && (
                <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
                  Finished {timeAgo(run.completed_at)}
                </span>
              )}
              {showGenericRunDetails && durationLabel && (
                <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
                  Duration {durationLabel}
                </span>
              )}
              {showGenericRunDetails && run.campaign_title && (
                <span className="max-w-full break-words [overflow-wrap:anywhere] rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
                  Campaign {run.campaign_title}
                </span>
              )}
              {showGenericRunDetails && runPrimaryActionLabel && (
                <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
                  Primary action {runPrimaryActionLabel}
                </span>
              )}
            </>
          )}
          actions={(
            <>
              <nav aria-label="Run view" className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
                <span aria-current="page" className="rounded-md bg-brand-50 px-3 py-1.5 text-sm font-medium text-brand-700">Result</span>
                <Link to={buildRunViewPath(currentRun.id, "trace", runBrandId, location.search)} className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Traces</Link>
              </nav>
              {savedActionRun?.actionId && <Link className="inline-flex min-h-11 items-center rounded-lg border border-slate-300 px-4 text-sm font-medium" to={`/brand/${encodeURIComponent(currentRun.brand_id)}/actions/${savedActionRun.origin}/${encodeURIComponent(savedActionRun.actionId)}`}>Open Action</Link>}
              {showGenericRunDetails && runPrimaryActionLabel
                ? (
              <button
                type="button"
                onClick={run.status === "failed" ? handleRetry : handleRerun}
                disabled={retrying}
                className="shrink-0 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {retrying ? (run.status === "failed" ? "Retrying…" : "Rerunning…") : runPrimaryActionLabel}
              </button>
                )
                : null}
            </>
          )}
        />
      </div>

      {retryError && (
        <p className="text-sm text-red-600" role="alert">{retryError}</p>
      )}

      {seoResearch && <SeoResearchReport status={seoResearch} />}
      {currentRun.collection && <SourceCollectionReport collection={currentRun.collection} brandId={currentRun.brand_id} datasetContext={currentRun.task_slug.startsWith("dataset_query_sync:")} />}

      {marketResearchRun && (
        <>
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" aria-labelledby="research-run-status">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-3xl">
                <h2 className="text-lg font-semibold text-slate-900" id="research-run-status">What happened</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">{researchReviewCopy.description}</p>
                {researchIdeasPath(currentRun) && (
                  <p className="mt-2 text-sm text-slate-700">
                    Ideas from this scan appear on the campaign's{" "}
                    <Link to={researchIdeasPath(currentRun)!} className="font-medium text-brand-700 underline">Opportunities</Link> tab.
                  </p>
                )}
                {researchQuestion && (
                  <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">{marketerPresentation.briefLabel}</p>
                    <p className="mt-1 text-sm leading-6 text-slate-800">{researchQuestion}</p>
                  </div>
                )}
              </div>
              {researchReview?.state === "needs_attention" && canUseRecovery("rerun") && (
                <div className="flex shrink-0 flex-wrap gap-2">
                  <button type="button" onClick={handleRerun} disabled={retrying} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
                    {retrying ? "Starting…" : "Rerun research"}
                  </button>
                </div>
              )}
            </div>

            {researchReview?.state === "ready_to_review" && (
              <div className="mt-5 border-t border-slate-100 pt-5">
                <h2 className="text-lg font-semibold text-slate-900">{marketerPresentation.resultLabel}</h2>
                {marketResearchOutput.isLoading ? (
                  <p className="mt-2 text-sm text-slate-600" role="status">Opening your report…</p>
                ) : researchOutputDocument ? (
                  <div className="mt-4"><OutputView document={researchOutputDocument} /></div>
                ) : researchOutputUnavailable ? (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" role="alert">
                    <p className="font-medium">The report is not available to open yet.</p>
                    <p className="mt-1">Reload once. If it remains unavailable, rerun the research; your recurring schedule will not change.</p>
                    <button type="button" onClick={() => void marketResearchOutput.refetch()} className="mt-3 font-medium text-amber-950 underline">Reload report</button>
                  </div>
                ) : null}
              </div>
            )}
          </section>
          <SupportingFindingsPanel label={marketerPresentation.evidenceLabel} findings={traceSupportingFindings} isLoading={traceLoading} error={traceError} />
        </>
      )}

      {currentEventsResearchRun && (() => {
        const question = currentEventsCampaignQuestion(currentRun);
        const outcome = currentEventsOutcome!;
        const needsAttention = outcome.state === "needs_attention" || outcome.state === "skipped";
        return (
          <>
          <section className={`rounded-2xl border bg-white p-5 shadow-sm ${needsAttention ? "border-amber-200" : outcome.state === "failed" ? "border-red-200" : "border-slate-200"}`} aria-labelledby="current-events-run-status">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-3xl">
                <h2 className="text-lg font-semibold text-slate-900" id="current-events-run-status">
                  {outcome.label}
                </h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  {outcome.state === "ready"
                    ? "This report separates dated observations from suggested campaign considerations. Review the sources before using it."
                    : outcome.description}
                </p>
                {question ? <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-4"><p className="text-xs font-semibold uppercase tracking-wide text-brand-700">{marketerPresentation.briefLabel}</p><p className="mt-1 text-sm leading-6 text-slate-800">{question}</p></div> : null}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {outcome.state === "ready" && currentEventsOutput?.route_path ? <Link to={currentEventsOutput.route_path} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">Open report</Link> : null}
                {(needsAttention && canUseRecovery("rerun")) || (outcome.state === "failed" && canUseRecovery("retry")) ? <button type="button" onClick={outcome.state === "failed" ? handleRetry : handleRerun} disabled={retrying} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">{retrying ? "Starting…" : outcome.state === "failed" ? "Retry research" : "Rerun research"}</button> : null}
              </div>
            </div>
          </section>
          <SupportingFindingsPanel label={marketerPresentation.evidenceLabel} findings={traceSupportingFindings} isLoading={traceLoading} error={traceError} />
          </>
        );
      })()}

      {blogGateFailure && (
        <section aria-labelledby="blog-gate-failure" className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950 shadow-sm">
          <h2 id="blog-gate-failure" className="text-lg font-semibold">Why the draft was blocked</h2>
          <div className="mt-3"><BlogGateFindings failure={blogGateFailure} /></div>
          {blogGateBriefHref && <Link to={blogGateBriefHref} className="mt-3 inline-flex min-h-11 items-center font-medium text-brand-700 underline">Open the brief to revise it or try again</Link>}
        </section>
      )}

      {showGenericRunDetails && <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-900">Run outcome</h2>
            {summary ? (
              <Markdown content={summary} className="mt-1 text-sm text-slate-600" />
            ) : (
              <p className="mt-1 break-words [overflow-wrap:anywhere] text-sm text-slate-600">
                {stateProjection?.summary
                  ?? run.error
                  ?? "Review the related work and technical details before deciding the next action."}
              </p>
            )}
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
            {runPresentation.job_label}
          </span>
        </div>
        <div className="mt-4 grid min-w-0 grid-cols-1 gap-3 md:grid-cols-3">
          <div className="min-w-0 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">State</p>
            <p className="mt-2 text-sm text-slate-800">
              {stateProjection?.label ?? humaniseSlug(currentRun.status)}
            </p>
          </div>
          <div className="min-w-0 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Impact</p>
            <p className="mt-2 break-words [overflow-wrap:anywhere] text-sm text-slate-800">
              {primaryOutput
                ? `Produced ${primaryOutput.title}`
                : reviewWorkProductHasDocument
                  ? "Produced operator review work"
                  : currentRun.collection
                    ? currentRun.collection.dataset
                      ? `Saved dataset: ${currentRun.collection.dataset.row_count} rows`
                      : currentRun.collection.state === "evidence_unavailable"
                      ? "Collection evidence unavailable"
                      : `${currentRun.collection.artifacts.length} verified collection artifact${currentRun.collection.artifacts.length === 1 ? "" : "s"}`
                  : currentRun.findings_count && currentRun.findings_count > 0
                    ? `Captured ${currentRun.findings_count} finding${currentRun.findings_count === 1 ? "" : "s"}`
                    : "No linked output recorded yet"}
            </p>
          </div>
          <div className="min-w-0 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Next action</p>
            <p className="mt-2 break-words [overflow-wrap:anywhere] text-sm text-slate-800">
              {recoveryAction?.detail
                ?? (reviewPath
                  ? "Open the related review surface if this Run produced draft or review work."
                  : primaryOutput
                    ? "Open the produced output when you need the actual artifact rather than the system record."
                    : "Open Traces when you need to inspect the recorded execution details.")}
            </p>
          </div>
        </div>
      </div>}

      {showGenericRunDetails && (workspaceBrandPath || campaignDetailPath || currentRun.brief_id || reviewPath || primaryOutput || traceSourceRuns.length > 0 || primarySourceFinding) && (
        <details className="rounded-xl border border-slate-200 bg-white p-5">
          <summary className="cursor-pointer text-sm font-semibold text-slate-900">Related work</summary>
          <div className="mt-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Related work</h2>
              <p className="mt-1 text-sm text-slate-600">
                Links and evidence related to this Run.
              </p>
            </div>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Related surfaces</p>
              <div className="mt-2 space-y-2 text-sm text-slate-700">
                {workspaceBrandPath && (
                  <p>
                    Workspace ·{" "}
                    <Link to={workspaceBrandPath} className="font-medium text-blue-700 hover:underline">
                      Open workspace home
                    </Link>
                  </p>
                )}
                {campaignDetailPath && (
                  <p>
                    Campaign ·{" "}
                    <Link to={campaignDetailPath} className="font-medium text-blue-700 hover:underline">
                      {currentRun.campaign_title ?? currentRun.campaign_id}
                    </Link>
                  </p>
                )}
                {currentRun.brief_id && (
                  <p>
                    Brief ·{" "}
                    {campaignDetailPath ? (
                      <Link to={campaignDetailPath} className="font-medium text-blue-700 hover:underline">
                        Open brief
                      </Link>
                    ) : (
                      <span className="font-medium text-slate-900">Brief available</span>
                    )}
                  </p>
                )}
                {reviewPath && (
                  <p>
                    Review ·{" "}
                    <Link to={reviewPath} className="font-medium text-blue-700 hover:underline">
                      Open approval surface
                    </Link>
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Lineage</p>
              <div className="mt-2 space-y-2 text-sm text-slate-700">
                {primaryOutput?.route_path ? (
                  <p>
                    Output ·{" "}
                    <Link to={buildBrandScopedPath(primaryOutput.route_path, brandId ?? currentRun.brand_id)} className="font-medium text-blue-700 hover:underline">
                      {primaryOutput.title}
                    </Link>
                  </p>
                ) : primaryOutput ? (
                  <p>Output · <span className="font-medium text-slate-900">{primaryOutput.title}</span></p>
                ) : null}
                {traceSourceRuns.length > 0 && (
                  <p>
                    Source Runs ·{" "}
                    {traceSourceRuns.slice(0, 2).map((sourceRun, index) => (
                      <span key={sourceRun.id}>
                        {index > 0 ? ", " : null}
                        <Link
                          to={buildBrandScopedPath(sourceRun.route_path, brandId ?? currentRun.brand_id)}
                          className="font-medium text-blue-700 hover:underline"
                        >
                          {taskRunPresentation(sourceRun).job_label}
                        </Link>
                      </span>
                    ))}
                    {traceSourceRuns.length > 2 ? `, +${traceSourceRuns.length - 2} more` : ""}
                  </p>
                )}
                {primarySourceFinding && (
                  <p>
                    Source finding ·{" "}
                    {primarySourceFindingHref ? (
                      <a href={primarySourceFindingHref} target="_blank" rel="noreferrer" className="font-medium text-blue-700 hover:underline">
                        {primarySourceFinding.title}
                      </a>
                    ) : (
                      <span className="font-medium text-slate-900">{primarySourceFinding.title}</span>
                    )}
                  </p>
                )}
                {traceSupportingFindings.length > 1 && (
                  <p className="text-xs text-slate-500">
                    {traceSupportingFindings.length} supporting findings attached to this Run.
                  </p>
                )}
              </div>
            </div>
          </div>
          </div>
        </details>
      )}

      {showGenericRunDetails && recoveryAction && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-amber-950">{recoveryAction.label}</h2>
              <p className="mt-1 text-sm text-amber-900">{recoveryAction.detail}</p>
            </div>
            {recoveryAction.kind === "link" && recoveryAction.href ? (
              <Link
                to={recoveryAction.href}
                className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
              >
                {recoveryAction.cta}
              </Link>
            ) : recoveryAction.kind === "retry" ? (
              <button
                type="button"
                onClick={handleRetry}
                disabled={retrying}
                className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {retrying ? "Retrying…" : recoveryAction.cta}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleRerun}
                disabled={retrying}
                className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {retrying ? "Rerunning…" : recoveryAction.cta}
              </button>
            )}
          </div>
        </div>
      )}

      {showGenericRunDetails && workProducts.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Work products</h2>
              <p className="mt-1 text-sm text-slate-600">
                Outputs recorded by this run. Open the canonical output page for content, trace, and native-record details.
              </p>
            </div>
          </div>
          <div className="mt-4 divide-y divide-slate-100 rounded-lg border border-slate-100">
            {workProducts.map((product) => (
              <div key={`${product.family}:${product.key}`} className="p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {product.route_path ? (
                    <Link to={buildBrandScopedPath(product.route_path, brandId)} className="text-sm font-medium text-blue-700 hover:underline">
                      {product.title}
                    </Link>
                  ) : (
                    <p className="text-sm font-medium text-slate-900">{product.title}</p>
                  )}
                  {(!showPageReadinessBadge || product.readiness !== currentRun.readiness || workProducts.length > 1) && (
                    <Badge variant={readinessVariant(product.readiness)}>
                      {readinessLabel(product.readiness) ?? product.readiness}
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {[humaniseDocumentType(product.output_type), humaniseSlug(product.family), shortLabel(product.brief_id, "Brief")]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {workProductReference(product) && (
                  <p className="mt-1 break-all text-xs text-slate-400">
                    {workProductReference(product)}
                  </p>
                )}
                {(product.warnings?.length ?? 0) > 0 && (
                  <p className="mt-1 text-xs text-amber-700">
                    Warnings: {product.warnings!.map(warningText).join("; ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Result keeps marketer-facing evidence above. The technical graph has a
          dedicated Trace route so it does not compete with the outcome. */}
      {showGenericRunDetails && (
        <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <h2 className="text-sm font-semibold text-slate-900">Need execution details?</h2>
          <p className="mt-1 text-sm text-slate-600">Open Traces to inspect recorded workflow activity, evidence lineage, warnings, and technical metadata.</p>
          <Link to={buildRunViewPath(currentRun.id, "trace", runBrandId, location.search)} className="mt-3 inline-block text-sm font-medium text-brand-700 hover:underline">Open Traces</Link>
        </section>
      )}
      {showGenericRunDetails && appliedCorrectionsCount > 0 && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          This Run considered {appliedCorrectionsCount} of your correction{appliedCorrectionsCount === 1 ? "" : "s"}.
        </div>
      )}
      {reviewWorkProductHasDocument && briefDetail && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">
                {briefDetail.document_type === "voice_review" ? "Voice review ready" : "Draft ready"}
              </h2>
              <p className="mt-1 text-sm text-slate-700">
                {briefDetail.document_type === "voice_review"
                  ? "This run produced voice-review recommendations for operator approval."
                  : briefDetail.document_type === "draft"
                    ? "This run produced draft copy for operator review."
                  : `This run is connected to a ${humaniseDocumentType(briefDetail.document_type).toLowerCase()} document.`}
              </p>
              <p className="mt-1 text-xs text-slate-600">
                {[briefDetail.campaign_title, briefDetail.brief_id ? `Brief ${briefDetail.brief_id.slice(0, 8)}` : null, briefDetail.document_key ? humaniseDocumentType(briefDetail.document_type) : null]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            <Link
              to={buildBrandScopedPath(`/review/${run.id}`, brandId)}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {briefDisplaySpec?.primaryCta.label ?? "Open draft for review"}
            </Link>
          </div>
          {briefDetail.content?.trim() && (
            <div className="mt-4 rounded-lg border border-blue-100 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {briefDetail.document_type === "voice_review" ? "Review preview" : "Draft preview"}
              </p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
                {truncatePreview(briefDetail.content)}
              </p>
            </div>
          )}
        </div>
      )}

      {reviewWorkProductMissingDocument && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <h2 className="text-sm font-semibold text-amber-950">
            {contentDraftRun ? "Draft output is not linked" : "Review output is not linked"}
          </h2>
          <p className="mt-1 text-sm text-amber-900">
            This {contentDraftRun ? "content-draft" : "voice-review"} run completed, but the run detail API could not resolve a {contentDraftRun ? "draft" : "review"} document yet.
          </p>
          {resolverInfo.reason && (
            <p className="mt-2 text-xs font-medium text-amber-900">
              Resolver reason: {resolverInfo.reason.replace(/_/g, " ")}
            </p>
          )}
          {(workProductDocumentKey || workProductBriefId) && (
            <p className="mt-2 text-xs text-amber-800">
              {[workProductBriefId ? `Brief ${workProductBriefId}` : null, workProductDocumentKey ? `Document ${workProductDocumentKey}` : null]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
          {resolverInfo.details && (
            <pre className="mt-3 overflow-auto rounded-lg bg-amber-100/70 p-3 text-xs text-amber-950">
              {JSON.stringify(resolverInfo.details, null, 2)}
            </pre>
          )}
          <p className="mt-2 text-xs text-amber-800">
            Check the run output and brief storage linkage before treating this as a usable draft.
          </p>
        </div>
      )}

      {briefDetail && !reviewWorkProductRun && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">Generated brief</h2>
              <p className="mt-1 text-sm text-slate-600">
                {briefDetail.title}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {humaniseDocumentType(briefDetail.document_type)} · {briefDetail.findings_count} finding{briefDetail.findings_count === 1 ? "" : "s"}
              </p>
            </div>
            <Link to={buildBrandScopedPath(`/review/${run.id}`, brandId)} className="text-sm font-medium text-blue-600 hover:underline">
              Open brief context
            </Link>
          </div>
        </div>
      )}

      {/* Failure callout */}
      {showGenericRunDetails && run.status === "failed" && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-800">Run failed</p>
          <p className="mt-1 text-sm text-red-700">
            {run.error ?? "No error message recorded."}
          </p>
        </div>
      )}

      {showGenericRunDetails && stateProjection?.stale_run && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">Recovered stale run</p>
          <p className="mt-1 text-sm text-amber-800">
            {stateProjection.summary ?? "This run was marked stale so it no longer blocks same-day work."}
          </p>
        </div>
      )}

      {showGenericRunDetails && ["evidence_unavailable", "collection_partial"].includes(stateProjection?.operator_state ?? "") && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">{stateProjection?.label}</p>
          <p className="mt-1 text-sm text-amber-800">{stateProjection?.summary}</p>
        </div>
      )}

      {showGenericRunDetails && stateProjection?.operator_state === "completed_noop" && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-900">Completed with no action taken</p>
          <p className="mt-1 text-sm text-slate-700">
            {stateProjection.summary ?? "This run completed cleanly but had nothing eligible to do."}
          </p>
          {(stateProjection.no_op_reason || stateProjection.prerequisite) && (
            <p className="mt-2 text-xs text-slate-500">
              {[stateProjection.no_op_reason, stateProjection.prerequisite].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
      )}

      {showGenericRunDetails && (() => {
        // Hide each panel independently so an empty params `{}` / `[]` doesn't
        // waste a column and a missing summary doesn't show a placeholder.
        const hasParams = hasRenderableInput(run.input);
        if (!summary && !hasParams) return null;
        return (
          <div className={`grid min-w-0 grid-cols-1 gap-4 ${summary && hasParams ? "lg:grid-cols-2" : ""}`}>
            {summary && (
              <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-5">
                <h2 className="text-sm font-semibold text-slate-800">Summary</h2>
                <Markdown content={summary} className="mt-2 text-sm text-slate-600" />
              </div>
            )}
            {hasParams && (
              <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-5">
                <h2 className="text-sm font-semibold text-slate-800">Params</h2>
                <pre className="mt-2 max-w-full overflow-auto whitespace-pre rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
                  {JSON.stringify(run.input, null, 2)}
                </pre>
              </div>
            )}
          </div>
        );
      })()}

      {run.status === "completed" && socialAction && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Next step</h2>
              <p className="mt-1 text-sm text-slate-700">
                {socialListeningNextStepCopy}
              </p>
            </div>
            {canSaveToCorpus && (
              <button
                type="button"
                onClick={() => void handleSaveToCorpus()}
                disabled={addToCorpus.isPending || addToCorpus.isSuccess}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {addToCorpus.isSuccess ? "Saved to Corpus" : addToCorpus.isPending ? "Saving…" : "Save to Corpus"}
              </button>
            )}
          </div>
          {addToCorpus.isSuccess && (
            <p className="mt-3 text-sm text-emerald-700">
              Saved to Corpus. Open{" "}
              <Link to={`/corpus?brandId=${encodeURIComponent(run.brand_id)}`} className="font-medium underline">
                Corpus
              </Link>{" "}
              to review it.
            </p>
          )}
          {addToCorpus.isError && (
            <p className="mt-3 text-sm text-red-700" role="alert">
              {addToCorpus.error instanceof Error ? addToCorpus.error.message : "Could not save this run to Corpus."}
            </p>
          )}
          {socialAction.notableThreads.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Notable threads</p>
              {socialAction.notableThreads.slice(0, 3).map((thread, index) => {
                const safeHref = safeExternalHref(thread.url);
                return (
                  <div key={`${thread.url ?? thread.title ?? "thread"}-${index}`} className="rounded-lg border border-blue-100 bg-white p-3">
                  <p className="text-sm font-medium text-slate-900">{thread.title ?? "Untitled thread"}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {[thread.platform, thread.reason].filter(Boolean).join(" · ")}
                  </p>
                  {safeHref && (
                    <a
                      href={safeHref}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-block text-sm font-medium text-blue-600 hover:underline"
                    >
                      View thread
                    </a>
                  )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showGenericRunDetails && (
        <FindingsList runId={run.id} expectedCount={run.findings_count ?? 0} brandId={run.brand_id} />
      )}
    </div>
  );
}
